import { Loader2 } from 'lucide-react'
import { createContext, type ReactNode, useContext, useEffect, useRef } from 'react'

interface HistoryPaginationContextValue {
  hasMore: boolean
  isFetchingMore: boolean
  fetchMore: () => void
  /** True while a pagination transaction is in flight (import + scroll
   * restore). The thread viewport reads this to disable `autoScroll` for
   * the duration so its own anchoring pass doesn't fight our restore. */
  isPaging: boolean
}

const HistoryPaginationContext = createContext<HistoryPaginationContextValue | null>(null)

export function useHistoryPagination(): HistoryPaginationContextValue | null {
  return useContext(HistoryPaginationContext)
}

export function HistoryPaginationProvider({
  value,
  children,
}: {
  value: HistoryPaginationContextValue
  children: ReactNode
}) {
  return (
    <HistoryPaginationContext.Provider value={value}>{children}</HistoryPaginationContext.Provider>
  )
}

/** Auto-loads the next older page when the user scrolls the sentinel
 * into view.
 *
 * Race-condition guard: aggressive scrolling can produce multiple
 * IntersectionObserver fires in the window between fetch completion
 * and `isPaging` flipping true (one render tick). React state-based
 * gates (`isFetchingMore`, `isPaging`, the old cooldown state) all
 * propagate asynchronously, so they leak. We use a synchronous ref
 * mutex on the observer callback as the source of truth: the first
 * intersection sets the mutex synchronously, all subsequent
 * intersections short-circuit until the transaction ends. The mutex
 * is released by a follow-up effect once both `isFetchingMore` and
 * `isPaging` settle to false (plus a short cooldown so the user must
 * scroll to retrigger). */
const COOLDOWN_MS = 600

export function HistoryLoadMoreSentinel() {
  const ctx = useContext(HistoryPaginationContext)
  const ref = useRef<HTMLDivElement | null>(null)
  const inFlightRef = useRef(false)
  const cooldownRef = useRef(false)

  // Synchronous mutex resolver: release inFlight once the full
  // transaction has settled and a short cooldown has elapsed.
  useEffect(() => {
    if (!ctx) return
    if (!inFlightRef.current) return
    if (ctx.isFetchingMore || ctx.isPaging) return
    // Transaction done — hold the lock for COOLDOWN_MS, then release.
    cooldownRef.current = true
    const t = setTimeout(() => {
      cooldownRef.current = false
      inFlightRef.current = false
    }, COOLDOWN_MS)
    return () => clearTimeout(t)
  }, [ctx])

  useEffect(() => {
    if (!ctx?.hasMore) return
    const node = ref.current
    if (!node) return
    const obs = new IntersectionObserver(
      entries => {
        if (!entries.some(e => e.isIntersecting)) return
        // Synchronous lock — no other intersection fire can race past
        // this branch even within the same frame.
        if (inFlightRef.current || cooldownRef.current) return
        inFlightRef.current = true
        ctx.fetchMore()
      },
      { rootMargin: '200px 0px 0px 0px' }
    )
    obs.observe(node)
    return () => obs.disconnect()
  }, [ctx])

  if (!ctx?.hasMore && !ctx?.isFetchingMore && !ctx?.isPaging) return null

  return (
    <div
      ref={ref}
      className="flex h-8 w-full items-center justify-center text-xs text-muted-foreground"
    >
      {ctx.isFetchingMore || ctx.isPaging ? <Loader2 className="size-3 animate-spin" /> : null}
    </div>
  )
}
